import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Languages } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SettingsModalLayout } from "@/features/user-setting/components/SettingsModalLayout";
import { LANG_EN, LANG_JP } from "@/constants";
import { toast } from "@/hooks/useToast";
import { useUser } from "@/hooks/useUser";
import type { ProfileUpdateRequest } from "@/lib/humhub-api/types";

interface LanguageModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const getLanguageFormSchema = () =>
  z.object({
    language: z.string(),
  });

export const LanguageModal: React.FC<LanguageModalProps> = ({
  isOpen,
  onClose,
}) => {
  const languageFormId = React.useId();
  const { t } = useTranslation();
  const { currentUser, updateUserProfile, isUpdating } = useUser();

  const languageFormSchema = React.useMemo(() => getLanguageFormSchema(), []);
  type LanguageFormValues = z.infer<typeof languageFormSchema>;

  const form = useForm<LanguageFormValues>({
    resolver: zodResolver(languageFormSchema),
    defaultValues: {
      language: currentUser?.account?.language || LANG_JP,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, errors: formErrors },
  } = form;

  React.useEffect(() => {
    if (isOpen) {
      reset({
        language: currentUser?.account?.language || LANG_JP,
      });
    }
  }, [currentUser, isOpen, reset]);

  if (!isOpen) return null;

  const onSubmit = async (data: LanguageFormValues) => {
    try {
      const updateData: ProfileUpdateRequest = {
        account: {
          language: data.language,
        },
      };

      if (isDirty) {
        await updateUserProfile(updateData);
      }

      toast({
        title: t("common.success"),
        description: t("common.profileUpdated"),
      });

      onClose();
    } catch (error) {
      console.error("Failed to update language:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("common.profileUpdateFailed");
      form.setError("root", { type: "manual", message: errorMessage });
    }
  };

  return (
    <SettingsModalLayout
      isOpen={isOpen}
      onClose={onClose}
      icon={Languages}
      maxWidth="max-w-md"
      title={t("common.language")}
      footer={
        <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form={languageFormId}
            variant="teal"
            disabled={isUpdating || !isDirty}
          >
            {isUpdating ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      }
    >
      <Form {...form}>
        <form
          id={languageFormId}
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="space-y-6">
            {formErrors.root && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                <AlertCircle className="size-4" />
                <span className="text-sm">{formErrors.root.message}</span>
              </div>
            )}

            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common.language")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("common.selectLanguage")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={LANG_JP}>
                        {t("common.japanese")}
                      </SelectItem>
                      <SelectItem value={LANG_EN}>English</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>
    </SettingsModalLayout>
  );
};
